/**
 * downloadReportVersion — Phase 2: replaces legacy cloud callables.
 *
 * Calls GET /api/reports/{reportId}/versions/{version} on dash-api.
 * Returns PM JSON in the same shape the Cloud Function used to return:
 *   { pmJson: {...}, version: number, reportId: string, ... }
 */

import {getAwpClientHeaders} from '../../session/awpRequestHeaders';

const AWP_BASE = process.env.REACT_APP_AWP_API_BASE || '';

/**
 * Fetch the list of available version numbers for a report (sorted DESC).
 * Returns an empty array if the report has no versions yet.
 *
 * @param {string} reportId
 * @returns {Promise<Array<{version: number, sizeBytes: number, createdAt: string}>>}
 */
export async function listReportVersionsFromApi(reportId) {
    const res = await fetch(
        `${AWP_BASE}/api/reports/${encodeURIComponent(reportId)}/versions`,
        {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                ...getAwpClientHeaders(),
            },
        },
    );
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
}

/**
 * Download the latest persisted version for a report from the REST API.
 * Returns null if no versions exist.
 *
 * @param {string} reportId
 * @returns {Promise<{content: object, references: Array, metadata: object|null}|null>}
 */
export async function downloadLatestVersionFromApi(reportId) {
    const versions = await listReportVersionsFromApi(reportId);
    if (!versions.length) return null;
    // Versions are returned DESC by the backend; first entry is the latest.
    const latestVersion = versions[0].version;
    const payload = await downloadReportVersionFromApi(reportId, latestVersion);
    const {references, metadata, version: _v, reportId: _r, ...content} = payload;
    return {content, references: references || [], metadata: metadata || null};
}

/**
 * @param {string|number} reportId
 * @param {string|number} version  — numeric version (zero-padded strings like "0000136" are coerced)
 * @returns {Promise<{pmJson: object, version: number, reportId: string, sizeBytes: number, createdAt: string}>}
 */
/**
 * Restore the live report document to a historical version snapshot.
 * Creates a new latest version and clears incremental Yjs updates server-side.
 *
 * @param {string} reportId
 * @param {string|number} version
 * @returns {Promise<{reportId: string, restoredFromVersion: number, newVersion: number}>}
 */
export async function restoreReportVersionFromApi(reportId, version) {
    const versionNum = parseInt(String(version), 10);
    const res = await fetch(
        `${AWP_BASE}/api/reports/${encodeURIComponent(reportId)}/versions/${versionNum}/restore`,
        {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                ...getAwpClientHeaders(),
            },
        },
    );

    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const error = new Error(err.message || `HTTP ${res.status}`);
        error.status = res.status;
        throw error;
    }

    return res.json();
}

export async function downloadReportVersionFromApi(reportId, version) {
    const versionNum = parseInt(String(version), 10);
    const res = await fetch(
        `${AWP_BASE}/api/reports/${encodeURIComponent(reportId)}/versions/${versionNum}`,
        {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                ...getAwpClientHeaders(),
            },
        },
    );

    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const error = new Error(err.message || `HTTP ${res.status}`);
        error.status = res.status;
        throw error;
    }

    const data = await res.json();

    // The backend now returns referenceMarks as a pre-serialized array (persisted by
    // ws-collaboration alongside the PM JSON snapshot). Include it as `references` so
    // useVersionBlob and the preview page can render citation data without a live Yjs
    // connection.
    const references = Array.isArray(data.referenceMarks) ? data.referenceMarks : [];
    const metadata = data.metadata && typeof data.metadata === 'object' ? data.metadata : null;

    // Normalise: the dash-api response wraps pmJson; callers expect the PM JSON
    // to be the top-level object (same shape as the old Cloud Function).
    return {
        ...data.pmJson,
        references,
        metadata,
        version: data.version ?? versionNum,
        reportId: data.reportId ?? reportId,
    };
}

/**
 * Compute a block-level diff between two report versions.
 * Calls GET /api/reports/{reportId}/versions/diff?from=&to= on dash-api, which proxies to
 * ydoc-version-manager's computeDiffByVersionNumbers (paragraph/heading blocks keyed by blockId;
 * comparison is text-only, formatting/marks are ignored).
 *
 * @param {string} reportId
 * @param {string|number} fromVersion - base/older version (0 diffs from an empty document)
 * @param {string|number} toVersion - target/newer version
 * @returns {Promise<{diff: {added: Array, removed: Array, changed: Array}, currentVersion: Array}>}
 */
export async function fetchVersionDiffFromApi(reportId, fromVersion, toVersion) {
    const from = parseInt(String(fromVersion), 10);
    const to = parseInt(String(toVersion), 10);
    const res = await fetch(
        `${AWP_BASE}/api/reports/${encodeURIComponent(reportId)}/versions/diff?from=${from}&to=${to}`,
        {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                ...getAwpClientHeaders(),
            },
        },
    );

    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const error = new Error(err.message || `HTTP ${res.status}`);
        error.status = res.status;
        throw error;
    }

    return res.json();
}
